// Key UI copy and strings (English, CLAUDE.md §0).

export const SEVERITY = {
  critical: { label: 'Critical', color: 'error', rank: 5 },
  high: { label: 'High', color: 'warning', rank: 4 },
  medium: { label: 'Medium', color: 'primary', rank: 3 },
  warning: { label: 'Warning', color: 'warning', rank: 2 },
  low: { label: 'Low', color: 'default', rank: 1 },
  info: { label: 'Info', color: 'default', rank: 0 },
};

export const SECTION_STATUS = {
  empty: 'Empty',
  draft: 'Draft',
  needs_review: 'Needs review',
  attention: 'Attention',
  draft_in_chat: 'Draft in chat',
  approved: 'Approved',
};

export const REJECT_TAGS = [
  { value: 'already_present', label: 'Already in the text' },
  { value: 'too_generic', label: 'Too generic' },
  { value: 'incorrect', label: 'Incorrect' },
  { value: 'not_relevant', label: 'Not relevant to the course' },
  { value: 'other', label: 'Other' },
];

export const REFINE_HINTS = ['Shorter', 'More specific', 'Plainer language', 'Add a Ukrainian case'];

export const ANALYSIS_STAGES = [
  { key: 'extract', label: 'Reading your file' },
  { key: 'parse', label: 'Matching sections' },
  { key: 'checks', label: 'Checking numbers' },
  { key: 'review', label: 'Reviewing wording' },
  { key: 'done', label: 'Done' },
];

export function confirmLabel(values = []) {
  if (!values.length) return 'Confirm';
  const shown = values.slice(0, 2).map((v) => v.value).join(', ');
  const rest = values.length - 2;
  return `Confirm · including ${shown}${rest > 0 ? ` and ${rest} more` : ''}`;
}

export const sectionsApproved = (approved, required) => `${approved} of ${required} sections approved`;

export const S = {
  appName: 'KSE Syllabus Studio',
  structure: 'Structure',
  chat: 'Chat',
  document: 'Live Document',
  downloadDocx: 'Download .docx',
  downloading: 'Preparing…',
  showChanges: 'Changes',
  cleanView: 'Clean',
  proposedText: 'Proposed text',
  documentHint: 'Exports current draft built strictly from the KSE master template.',
  documentEmpty: 'This section is not filled yet — upload an existing syllabus or ask AI to draft it.',
  documentLoadError: 'Could not load the live document.',
  readiness: 'Readiness',
  approve: 'Approve section',
  unapprove: 'Reopen',
  approveAll: 'Approve all',
  approved: 'Approved',
  skipped: 'Skipped',
  skip: 'Skip',
  whyWeAsk: 'Why this is needed',
  gaps: 'Pending items',
  openIssues: 'Checks',
  pendingDrafts: (n) => `${n} draft${n === 1 ? '' : 's'} to review`,
  loadingConversation: 'Loading conversation…',
  thinking: 'AI is reviewing and updating sections…',
  thinkingLong: 'Still working on complex sections — you can browse the document while waiting.',
  chatPlaceholder: 'Ask, or tell me what to change…',
  chatCaption: 'Nothing changes until you confirm it.',
  stop: 'Stop',
  send: 'Send',
  noMessages: 'No messages yet in this consultation.',
  newSyllabus: 'New syllabus',
  untitled: 'Untitled Course',
  syllabus: 'Syllabus',
  before: 'Current version',
  after: 'Proposed version',
  proposed: 'Proposed',
  changeValue: 'Change the value',
  reword: 'Reword',
  confirmLabel: 'Confirm',
  cancel: 'Cancel',
  back: 'Back',
  save: 'Save & apply',
  gotIt: 'Acknowledge',
  undo: 'Undo',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  sendAnswer: 'Submit answer',
  yourAnswer: 'Your response',
  writeOwn: 'Or type your own answer…',
  writeOwnPolicy: 'Write my own',
  closestMatch: 'Closest to how you already work',
  yourCurrentText: 'Your current text',
  customText: 'Custom adaptation',
  chooseOption: 'Select a policy option to apply to this syllabus:',
  chosenOption: 'Selected',
  advisory: 'Advisory recommendation',
  staleDraft: 'This section changed since the draft was written.',
  draftAgain: 'Draft again',
  draftFooter: 'My wording, from what you just said. Change any of it.',
  valuesHint: 'Values suggested by AI based on course parameters. Verify before confirming.',
  pickValue: 'Select a value to adjust:',
  noOptions: 'No presets available.',
  noChangesTitle: (n) => `${n} section${n === 1 ? '' : 's'} need no changes`,
  noChangesHint: 'Approve all matching sections at once, or review each in the Live Document.',
  approveAllSkipped: 'Could not approve automatically:',
  question: 'Clarification Needed',
  rewordTitle: 'Instruct AI to revise draft',
  rewordHint: 'Explain how you want this draft adjusted. The prior revision remains in history.',
  rewordPlaceholder: 'E.g., tighten word count, emphasize ethical implications, align with capstone…',
  comment: 'Guidance',
  rewording: 'Revising draft…',
  updateDraft: 'Submit revision request',
  cancelTitle: 'Dismiss this recommendation',
  cancelReasonRequired: 'A brief rationale is required for dismissing high-priority checks.',
  cancelReasonOptional: 'Optional rationale to improve subsequent recommendations:',
  reason: 'Reason for dismissal',
  reasonRequired: 'Reason (required)',
  rebuildBanner: 'This syllabus was made in an older version.',
  rebuild: 'Rebuild syllabus',
  readOnlyBanner: 'Read-only mode: you are viewing another instructor’s syllabus as Administrator.',
  legacyBanner: 'Legacy syllabus archive: download or re-import to enable live editing.',
  uploadAgain: 'Re-upload',
  mocksBanner: 'Demo preview mode (REACT_APP_USE_MOCKS=1): changes are kept in local memory.',
  analyzingTitle: 'Analyzing your course syllabus…',
  analyzingHint: 'Cross-checking with KSE templates and programmatic outcomes. This takes up to 45 seconds.',
  analysisFailed: 'Analysis encountered an issue.',
  analysisTimedOut: 'Analysis timed out. You may open the workspace directly or retry.',
  retryAnalysis: 'Retry analysis',
  analysisProgress: 'Syllabus Alignment Progress',
  welcomeTitle: 'Let’s build your syllabus',
  welcomeHint: 'Align your syllabus with Kyiv School of Economics academic standards and accreditation criteria.',
  fromScratch: 'Start from scratch',
  fromScratchHint: 'Define course parameters and let the AI Assistant draft sections collaboratively.',
  haveDraft: 'Drop your draft',
  haveDraftHint: 'I\'ll read the file and match it to the KSE template.',
  courseTitle: 'Course title',
  courseCode: 'Course code (optional)',
  program: 'Academic programme',
  edition: 'Cohort / Academic year',
  selectProgram: 'Select academic programme',
  noPrograms: 'No programmes configured',
  loadingPrograms: 'Loading programmes…',
  create: 'Start drafting',
  creating: 'Creating…',
  uploadAndAnalyze: 'Upload & analyze',
  uploading: 'Uploading document…',
  dropHere: 'Drop your file here',
  dropOrClick: 'Drop your draft here or click to browse',
  fileFormats: 'DOCX, DOC, PDF · up to 10 MB',
  fileHint: 'Word (.docx, .doc) or PDF. The result is a .docx in the KSE template.',
  errCourseTitle: 'Please enter the course title.',
  errProgram: 'Please select an academic programme.',
  errEdition: 'Please select a programme cohort.',
  errProgramsLoading: 'Programmes are still loading. Please wait a moment.',
  errNoPrograms: 'At least one programme is required to create a syllabus.',
  errLoadPrograms: 'Could not load programmes.',
  errCreate: 'Could not initialize syllabus workspace.',
  errUpload: 'Could not upload document.',
  errLoad: 'Could not load syllabus.',
  errAction: 'Action could not be completed. Please try again.',
  errDownload: 'Could not generate the .docx file right now. Please try again in a moment.',
  errRebuild: 'Could not rebuild syllabus.',
  errTimeout: 'Server request timed out. Please try again.',
};

// 14 concrete error scenarios matching the spec
export const ERROR_MESSAGES = {
  STATE_CONFLICT: 'The syllabus changed in another tab or action. Refreshing to the latest version…',
  STALE_DRAFT: 'This section changed since the draft was written.',
  UNCONFIRMED_VALUES: 'Please review and confirm the highlighted values before proceeding.',
  SECTION_INCOMPLETE: 'Please resolve the highlighted items in this section before approving.',
  SECTION_HAS_BLOCKERS: 'Please resolve the highlighted items in this section before approving.',
  ANALYSIS_IN_PROGRESS: 'Syllabus is currently being analyzed. Please wait a moment.',
  ANSWER_REQUIRED: 'Please answer the question before confirming.',
  LOCKED: 'This part is fixed by the school and can\'t be edited.',
  NEEDS_REBUILD: 'This syllabus was made in an older version.',
  REOPEN_CONFLICT: 'This section was changed later, so it can\'t be reopened automatically.',
  REASON_REQUIRED: 'Please provide a brief reason to dismiss a critical suggestion.',
  NOT_PENDING: 'This was already handled.',
  INVALID_PROGRAM_CODE: 'Select a valid academic programme and cohort.',
  UNSUPPORTED_FORMAT: 'We accept DOCX, DOC and PDF.',
  FILE_TOO_LARGE: 'The file is larger than 10 MB. Try saving it without large images.',
  CORRUPTED_FILE: 'We couldn’t extract readable text from this file. Check that the .docx isn’t password-protected or empty.',
  LEGACY_SYLLABUS: 'This syllabus is read-only. Upload the file again to work on it.',
  NOT_IMPLEMENTED: 'This feature is not available yet.',
  PERMISSION_DENIED: 'You don’t have permission to modify this syllabus.',
};
